import type { EntityType, MasterEntityType } from '@api/entity-attributes';

export interface CoreColumnConfig {
    key: string;
    header: string;
    /** Whether this column can be edited inline */
    editable: boolean;
}

export interface EntityConfig {
    entityType: MasterEntityType;
    /** URL param name used in PATCH calls */
    patchPath: (id: string) => string;
    searchPlaceholder: string;
    coreColumns: CoreColumnConfig[];
}

export const ENTITY_CONFIG: Record<MasterEntityType, EntityConfig> = {
    PRODUCT: {
        entityType: 'PRODUCT',
        patchPath: (id) => `/products/${id}`,
        searchPlaceholder: 'Search by name or SKU…',
        coreColumns: [
            { key: 'name',         header: 'Name',     editable: true },
            { key: 'sku',          header: 'SKU',       editable: false },
            { key: 'category',     header: 'Category',  editable: true },
            { key: 'productGroup', header: 'Group',     editable: true },
            { key: 'productType',  header: 'Type',      editable: true },
            { key: 'price',        header: 'Price',     editable: true },
            { key: 'unitsPerCase', header: 'Case Size', editable: true },
            { key: 'isActive',     header: 'Active',    editable: true },
            { key: 'vendors',      header: 'Vendors',   editable: false },
        ],
    },
    WAREHOUSE: {
        entityType: 'WAREHOUSE',
        patchPath: (id) => `/warehouses/${id}`,
        searchPlaceholder: 'Search warehouses…',
        coreColumns: [
            { key: 'name',        header: 'Name',         editable: true },
            { key: 'city',        header: 'City',         editable: true },
            { key: 'state',       header: 'State',        editable: true },
            { key: 'country',     header: 'Country',      editable: true },
            { key: 'type',        header: 'Type',         editable: true },
            { key: 'minCapacity', header: 'Min Capacity', editable: true },
            { key: 'maxCapacity', header: 'Max Capacity', editable: true },
            { key: 'isActive',    header: 'Active',       editable: true },
        ],
    },
    CHANNEL: {
        entityType: 'CHANNEL',
        patchPath: (id) => `/channels/${id}`,
        searchPlaceholder: 'Search channels…',
        coreColumns: [
            { key: 'name',            header: 'Name',        editable: true },
            { key: 'priority',        header: 'Priority',    editable: true },
            { key: 'model',           header: 'Model',       editable: true },
            { key: 'type',            header: 'Type',        editable: true },
            { key: 'abbreviationKey', header: 'Abbrev. Key', editable: true },
        ],
    },
    VENDOR: {
        entityType: 'VENDOR',
        patchPath: (id) => `/vendors/${id}`,
        searchPlaceholder: 'Search by name or code…',
        coreColumns: [
            // The code is the primary key and the join key across purchase orders,
            // vendor mappings and lanes — immutable, same stance as the product SKU.
            { key: 'vendorId',     header: 'Vendor ID',     editable: false },
            { key: 'name',         header: 'Vendor Name',   editable: true },
            { key: 'street',       header: 'Street',        editable: true },
            { key: 'city',         header: 'City',          editable: true },
            { key: 'country',      header: 'Country',       editable: true },
            { key: 'zip',          header: 'Zip',           editable: true },
            { key: 'moq',          header: 'MoQ',           editable: true },
            { key: 'incoterms',    header: 'Incoterms',     editable: true },
            { key: 'email',        header: 'Email',         editable: true },
            { key: 'paymentTerms', header: 'Payment Terms (Notes)', editable: true },
        ],
    },
    VENDOR_PRODUCT_MAPPING: {
        entityType: 'VENDOR_PRODUCT_MAPPING',
        // Rows are addressed by the product+vendor pair, not a single id — there is
        // no REST path that names one mapping, so inline edits go through the
        // generic entity-master row endpoint instead.
        patchPath: (id) => `/product-vendor-mappings/${id}`,
        searchPlaceholder: 'Search by product or vendor…',
        coreColumns: [
            { key: 'productId',   header: 'Product ID',   editable: false },
            { key: 'productName', header: 'Product Name', editable: false },
            { key: 'category',    header: 'Category',     editable: false },
            { key: 'vendorId',    header: 'Vendor',       editable: false },
            { key: 'price',       header: 'Price',        editable: true },
            { key: 'currency',    header: 'Currency',     editable: true },
            { key: 'isActive',    header: 'Active',       editable: false },
        ],
    },
    VENDOR_MATERIAL_MAPPING: {
        entityType: 'VENDOR_MATERIAL_MAPPING',
        // Rows are addressed by the material+vendor pair, so there is no REST path
        // naming one mapping — inline edits go through the generic entity-master row
        // endpoint, exactly as the product mapping does.
        patchPath: (id) => `/vendor-material-mappings/${id}`,
        searchPlaceholder: 'Search by material or vendor…',
        coreColumns: [
            { key: 'materialId',   header: 'Material ID',   editable: false },
            { key: 'materialName', header: 'Material Name', editable: false },
            { key: 'category',     header: 'Category',      editable: false },
            { key: 'vendorId',     header: 'Vendor',        editable: false },
            { key: 'cost',         header: 'Cost',          editable: true },
            { key: 'currency',     header: 'Currency',      editable: true },
            { key: 'priority',     header: 'Priority',      editable: true },
            { key: 'isActive',     header: 'Active',        editable: false },
        ],
    },
    VENDOR_PRICE: {
        entityType: 'VENDOR_PRICE',
        // Rows are price lines with their own id, but edits go through the generic
        // entity-master row endpoint, which versions the price; there is no REST path here.
        patchPath: (id) => `/vendor-prices/${id}`,
        searchPlaceholder: 'Search by vendor or item…',
        coreColumns: [
            { key: 'vendorId',      header: 'Vendor',        editable: false },
            { key: 'itemType',      header: 'Type',          editable: false },
            { key: 'itemId',        header: 'Item ID',       editable: false },
            { key: 'itemName',      header: 'Item',          editable: false },
            { key: 'minQty',        header: 'From Qty',      editable: false },
            { key: 'terms',         header: 'Terms',         editable: false },
            { key: 'currency',      header: 'Currency',      editable: false },
            { key: 'price',         header: 'Price',         editable: true },
            { key: 'unit',          header: 'Per',           editable: false },
            { key: 'effectiveFrom', header: 'Since',         editable: false },
            { key: 'note',          header: 'Note',          editable: true },
            { key: 'priceHistory',  header: 'Price History', editable: false },
        ],
    },
    MATERIAL: {
        entityType: 'MATERIAL',
        patchPath: (id) => `/materials/${id}`,
        searchPlaceholder: 'Search by name or code…',
        coreColumns: [
            { key: 'name',         header: 'Name',     editable: true },
            // The code is the PK and the join key across BOM, vendor mappings and
            // stock — immutable, same stance as the product SKU.
            { key: 'sku',          header: 'Code',     editable: false },
            { key: 'category',     header: 'Category', editable: true },
            { key: 'materialType', header: 'Type',     editable: true },
            { key: 'cost',         header: 'Cost',     editable: true },
            { key: 'unit',         header: 'Unit',     editable: true },
            { key: 'vendors',      header: 'Vendors',  editable: false },
        ],
    },
};

export const PAGE_SIZE = 50;

/**
 * Whether this entity is a two-column mapping table rather than a thing with an id.
 *
 * Both vendor mapping tables are addressed by a PAIR — (product, vendor) and
 * (material, vendor) — which is what the generic bulk-update path cannot express: it
 * identifies a row by its first column alone. Their own Import CSV does the same job
 * with a real two-column key, so the bulk upload is suppressed for them.
 *
 * A predicate rather than the string comparisons this replaced: the same test was
 * written out three times against one entity type, and adding the second would have
 * made it six places to keep in step.
 */
export const isMappingEntity = (entityType: MasterEntityType): boolean =>
    entityType === 'VENDOR_PRODUCT_MAPPING' ||
    entityType === 'VENDOR_MATERIAL_MAPPING';

/**
 * The one column on each mapping grid that only an admin may change: the agreed price.
 * The server refuses anyone else's change to it, so the grid locks the cell up front
 * rather than letting a batch of edits fail on save over the one cell it would refuse.
 */
export const isMappingPriceColumn = (entityType: MasterEntityType, key: string): boolean =>
    (entityType === 'VENDOR_PRODUCT_MAPPING' && key === 'price') ||
    (entityType === 'VENDOR_MATERIAL_MAPPING' && key === 'cost') ||
    (entityType === 'VENDOR_PRICE' && key === 'price');

/**
 * The columns whose values are too long to edit inside a cell, so they open a popover
 * instead. Keyed by entity, because the same column name is not always long.
 *
 * Chosen from the data, not by eye — the longest live value in each editable text column
 * across all merchants:
 *
 *   vendor payment_terms  356   warehouse street  171   vendor street  141
 *   product name          185   channel name      133   everything else <= 55
 *
 * The two `name` columns are long only at the extreme (35 and 28 characters on average),
 * so they are deliberately left out: flagging them would make the common case heavier to
 * serve the outlier. Add one here if that turns out to be the wrong call — it is a
 * one-line change and nothing else needs to know.
 */
export const WIDE_TEXT_COLUMNS: Partial<
    Record<MasterEntityType, ReadonlySet<string>>
> = {
    VENDOR: new Set(['street', 'paymentTerms']),
    WAREHOUSE: new Set(['street']),
};

export const isWideTextColumn = (
    entityType: MasterEntityType,
    columnKey: string,
): boolean => WIDE_TEXT_COLUMNS[entityType]?.has(columnKey) ?? false;

/**
 * The grid's entity types are a SUPERSET of the ones custom attributes work on, which is
 * why `EntityType` and `MasterEntityType` are two unions rather than one.
 *
 * A custom attribute value is stored against a single `entity_id`. Vendor material
 * mappings are identified by a PAIR, so an attribute on one could only key on the
 * material — silently shared across every vendor supplying it, and wrong the moment a
 * second vendor appears. Rather than inherit that (the product mapping already has it),
 * this entity simply has no attribute support: no Add column, no attribute filters.
 *
 * Returns the narrowed `EntityType` where attributes apply, or null where they do not.
 */
export const attributeEntityType = (
    entityType: MasterEntityType,
): EntityType | null =>
    // Price lines are versions of a fact, not a thing to hang attributes on either.
    entityType === 'VENDOR_MATERIAL_MAPPING' || entityType === 'VENDOR_PRICE' ? null : entityType;

/**
 * Where an export header is not simply the snake_case of the column key.
 *
 * Empty, and worth keeping for the next exception rather than deleting. It briefly held
 * `MATERIAL: { sku: 'code' }`, to match the material create importer's `code` column — but
 * that column sets material_id AND sku at once, while the grid's `sku` is only the second of
 * them. The two are the same value for Healthfab and f353e3da and different for the 178 live
 * materials where they are not, so the header now says `sku` and means it.
 */
const EXPORT_HEADER_OVERRIDES: Partial<
    Record<MasterEntityType, Record<string, string>>
> = {};

/**
 * The header one column carries in an exported CSV.
 *
 * snake_case of the column key — which is the DB column name for core columns and the
 * already-slugified key for custom attributes. That is not a tidy coincidence, it is
 * the whole contract: PRODUCT_CSV_COLUMNS and MATERIAL_CSV_COLUMNS are snake_case, and
 * the backend's `normalizeCsvHeader` reduces incoming headers the same way. The grid's
 * own labels read better ("Weight (g)", "Group") and round-trip to nothing, which is
 * why they are deliberately not used here.
 */
export function exportHeaderFor(
    entityType: MasterEntityType,
    key: string,
): string {
    return (
        EXPORT_HEADER_OVERRIDES[entityType]?.[key] ??
        key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase()
    );
}

/**
 * What a custom attribute's column is called in an exported file.
 *
 * Namespaced, because an attribute's slug can land on a built-in column's. Nike has a
 * "Product Type" attribute keyed `product_type`, which is also the core column's key, and
 * writing both bare produced a file with two identically-named columns — where the empty
 * one silently won, destroying the real value on the way in. Attribute creation now refuses
 * new collisions, but the ones already in the database still have to round trip.
 *
 * Kept in step with ATTRIBUTE_COLUMN_PREFIX in product.service.ts, which reads these.
 */
export function attributeExportHeader(key: string): string {
    return `attr_${key}`;
}

/**
 * What the identity column is called in an exported file.
 *
 * Every export leads with the row's natural key — the value `entity_attribute_values`
 * stores in `entity_id`, and the value any importer has to match a row on. Leading with
 * it is what makes a file that comes back keyable at all: names collide, and a
 * spreadsheet that has been reordered has lost whichever column happened to be first.
 */
export const ENTITY_EXPORT_KEY_HEADER: Record<MasterEntityType, string> = {
    PRODUCT: 'product_id',
    VENDOR: 'vendor_id',
    VENDOR_PRODUCT_MAPPING: 'product_id',
    VENDOR_MATERIAL_MAPPING: 'material_id',
    VENDOR_PRICE: 'id',
    WAREHOUSE: 'warehouse_id',
    CHANNEL: 'channel_id',
    MATERIAL: 'material_id',
};
